function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import MI from '../../endpoints/market-intelligence';
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Delta, Switcher, Pending } from "../../components/analytics-kit";
import PlunkDropDown from "../../components/plunk-dropdown";
import StatusFilter from "../../components/status-filter";
import GeoChoropleth from "../../components/geo-choropleth";
import GeoMap from "../../components/geo-map";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import { hasTenantFeature } from "../../functions/tenant-features";
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { RANGES, num, rangeLabelFor, resolveBusinessModel, stateName, deltaOfPaced, apiComparison, channelLabel, fetchSales } from "./territory-common";
import { isInProgressRange, isAdminUser, decodeEntities, categoryLeaf } from "../stores/common";
import { getOverviewOrTotals, getRepScorecardsOrRoster, getDealerScorecardsOrRoster, getStoreScorecardsOrRoster, getCoverageOrTerritories, getGeoSummary, getGeoCoverage, getGeoDealers, getGeoHeat, getGeoStores, exportScorecardsCsv, exportCoverageCsv, lapseKey, lapseLabel, lapseTone, LAPSE_LEVELS, isSoft, getDemandGaps, getDealerPerformance } from "./sales-api";
import { usdK, usd0, lastOrderText, pct0, ratioDelta, Spark, AttainBar, PaceText, paceTone, HealthChips, BandChip, StateChips, CategoryChips, elapsedLabel, targetWindowLabel, isSalesUser } from "./exec-kit";
import { isHouseRep, isRosterRep } from "./territory-redesign-kit";
import { repSvc, safe } from "../reports/common";
import ScheduleSheet, { parseSchedule } from "../reports/schedule-sheet";

/*
 * Sales executive view: the corporate sales cockpit (SALES_EXECUTIVE_VIEW.md §5,
 * mockup sales-executive-view-2026-10-06.html).
 *
 * Route family /Sales/:range with the tabs Overview · Reps · Dealers (Stores on a
 * retail tenant) · Coverage · Targets. Every number is a pure read:
 *   /v1/sales/overview/{range}            the ribbon
 *   /v1/sales/scorecards/reps/{range}     rep scorecards (sparkline, bands, target inline)
 *   /v1/sales/scorecards/dealers/{range}  dealer scorecards
 *   /v1/sales/scorecards/stores/{range}   retail alias over /v1/store/performance
 *   /v1/sales/coverage/{range}            state table + flags
 *   /v1/sales/geo/*                       the maps (SALES_TERRITORY_MAPS.md §6)
 * Range + comparison are the global period store (rule 2); deltas use the paced
 * comparison for in-progress windows (rule 3); attainment and pace columns show
 * only when targets exist, otherwise the header says "Set targets" (rule 4).
 * The backend lands from the same spec in parallel: every call is fail-soft and
 * the roster rails stand in (source:"roster") until the scorecards are live.
 */

const KEY_CHANNEL = "plk_sales_exec_channel";
const COCKPIT_REPORT = "sales cockpit, weekly";
const kitDelta = d => d && d.pct != null ? {
  dir: d.dir,
  pct: d.pct
} : null;
const REP_SORTS = [["attainment", "Progress to goal"], ["pace", "Pace"], ["revenue", "Revenue"], ["newDealers", "New dealers"], ["atRisk", "Dealers slowing"]];
const DEALER_SORTS = [["band", "Health"], ["revenue", "Revenue"], ["attainment", "Progress to goal"], ["pace", "Pace"], ["lastOrder", "Last order"]];
// "this year" / "in the last 7 days" / "so far": the window in a sentence ("354 ordered for the first time this year").
function inWindow(range) {
  if (range === "all_time") return "so far";
  const l = String(rangeLabelFor(range, RANGES) || "").toLowerCase();
  if (!l || l === String(range).toLowerCase() || /\d/.test(l) && !/^last \d/.test(l)) return "in this period";
  return /^(this|today|yesterday)/.test(l) ? l : `in the ${l}`;
}
// Coverage flags in plain words (uncovered / overlap / no activity).
const flagLabel = f => f === "uncovered" ? "No rep" : f === "overlap" ? "Shared" : f === "no activity" ? "No sales" : f;
// A small "do this next" link under a strip tile.
const Go = ({
  to,
  onClick,
  children
}) => to ? /*#__PURE__*/React.createElement(Link, {
  className: "sc-go",
  to: to
}, children) : /*#__PURE__*/React.createElement("a", {
  className: "sc-go",
  onClick: onClick
}, children);
const tileSub = (text, go) => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
  className: "sc-sub"
}, text), go || null);
const BAND_RANK = {
  lapsing: 0,
  at_risk: 1,
  watch: 2,
  healthy: 3
};
class SalesExecutiveView extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "openMiMarket", z => {
      const name = this.miMarketFor(z);
      if (!name) return;
      this.props.history.push(`/MarketIntelligence/${this.range()}?location=${encodeURIComponent(name)}`);
      try {
        window.scrollTo(0, 0);
      } catch (e) {/* no window */}
    });
    _defineProperty(this, "onChannel", channel => {
      try {
        if (channel) localStorage.setItem(KEY_CHANNEL, channel);else localStorage.removeItem(KEY_CHANNEL);
      } catch (e) {/* ignore */}
      this.setState({
        channel
      }, () => this.reset(() => this.load()));
    });
    _defineProperty(this, "onStatus", status => this.setState({
      status: status || []
    }, () => this.reset(() => this.load())));
    // ---- drill paths (§5.5) ----
    _defineProperty(this, "openRep", code => code && this.props.history.push(`/Territory/${this.range()}/rep/${encodeURIComponent(code)}`));
    _defineProperty(this, "openDealer", id => id && this.props.history.push(`/Territory/${this.range()}/customer/${encodeURIComponent(id)}`));
    _defineProperty(this, "openStore", id => id && this.props.history.push(`/Stores/${this.range()}/store/${encodeURIComponent(id)}`));
    _defineProperty(this, "openRegion", code => code && this.props.history.push(`/Territory/${this.range()}/region/${encodeURIComponent(code)}`));
    _defineProperty(this, "openChannel", ch => ch && this.props.history.push(`/Territory/${this.range()}/channel/${encodeURIComponent(ch)}`));
    _defineProperty(this, "openCategory", (dealer, cat) => {
      if (cat && cat.productId) this.props.history.push(`/Territory/${this.range()}/product/${encodeURIComponent(cat.productId)}`);else if (dealer && dealer.id) this.props.history.push(`/Dealers/${this.range()}/dealer/${encodeURIComponent(dealer.id)}`);
    });
    // ---- report hooks (§7.3) ----
    _defineProperty(this, "exportCsv", async () => {
      const tab = this.tab();
      const range = this.range();
      const q = this.query();
      try {
        if (tab === "reps") await exportScorecardsCsv("reps", range, {
          ...q,
          channel: this.state.repChannel,
          region: this.state.repRegion,
          status: this.state.repStatus,
          onPlan: this.state.repOnPlan,
          sort: this.state.repSort
        });else if (tab === "dealers") await exportScorecardsCsv(this.isRetail() ? "stores" : "dealers", range, {
          ...q,
          rep: this.state.dlRep,
          region: this.state.dlRegion,
          channel: this.state.dlChannel,
          band: this.state.dlBand,
          stage: this.state.dlStage,
          sort: this.state.dlSort
        });else if (tab === "coverage") await exportCoverageCsv(range, q);else await exportScorecardsCsv("reps", range, q);
      } catch (e) {
        this.setState({
          note: isSoft(e) ? "CSV export is not available yet for this view; it arrives with the scorecard rail." : "Export failed. Try again."
        });
      }
    });
    _defineProperty(this, "openReport", () => {
      const retail = this.isRetail();
      const tab = this.tab();
      const ds = tab === "dealers" ? retail ? "stores" : "dealers" : "territory_reps";
      const f = [];
      if (tab === "reps") {
        if (this.state.repChannel) f.push(`filter.channel=${encodeURIComponent(this.state.repChannel)}`);
        if (this.state.repRegion) f.push(`filter.region=${encodeURIComponent(this.state.repRegion)}`);
        if (this.state.repStatus) f.push(`filter.status=${encodeURIComponent(this.state.repStatus)}`);
      }
      if (tab === "dealers") {
        if (this.state.dlRep) f.push(`filter.rep=${encodeURIComponent(this.state.dlRep)}`);
        if (this.state.dlRegion) f.push(`filter.region=${encodeURIComponent(this.state.dlRegion)}`);
        if (this.state.dlBand) f.push(`filter.band=${encodeURIComponent(this.state.dlBand)}`);
      }
      this.props.history.push(`/Reports/new?dataset=${ds}${f.length ? "&" + f.join("&") : ""}`);
    });
    // Schedule: the seeded starter "Sales cockpit, weekly" opens the existing schedule sheet; without it, the builder.
    _defineProperty(this, "openSchedule", async () => {
      if (this.state.scheduleBusy) return;
      this.setState({
        scheduleBusy: true
      });
      const d = await safe(repSvc("").getAsync(), null);
      if (!this._alive) return;
      const rows = d && (d.items || d) || [];
      const hit = (Array.isArray(rows) ? rows : []).find(r => String(r.name || "").toLowerCase().trim() === COCKPIT_REPORT || /sales cockpit/i.test(String(r.name || "")));
      if (hit) this.setState({
        scheduleBusy: false,
        sheet: {
          report: hit,
          schedule: parseSchedule(hit.scheduleJson)
        }
      });else {
        this.setState({
          scheduleBusy: false,
          note: "The starter report \"Sales cockpit, weekly\" is not seeded for this workspace yet. Build it from the Territory & Reps dataset and schedule it there."
        });
        this.openReport();
      }
    });
    let _channel = "";
    try {
      _channel = localStorage.getItem(KEY_CHANNEL) || "";
    } catch (e) {/* no storage */}
    this.state = {
      bm: null,
      comp: getGlobalComp(),
      channel: _channel,
      status: [],
      overview: undefined,
      reps: undefined,
      dealers: undefined,
      stores: undefined,
      coverage: undefined,
      channels: null,
      gaps: undefined,
      geoSummary: undefined,
      geoCoverage: undefined,
      geoDealers: undefined,
      geoHeat: undefined,
      geoStores: undefined,
      geoLoaded: false,
      // per-tab filters (client-side over the loaded envelope; the CSV export sends the same keys)
      repChannel: "",
      repRegion: "",
      repStatus: "active",
      repOnPlan: "",
      repSort: "attainment",
      dlRep: "",
      dlRegion: "",
      dlChannel: "",
      dlBand: "",
      dlStage: "",
      dlSort: "band",
      dlNew: false,
      dlFewer: false,
      dlOther: false,
      covMetric: "revenue",
      covView: "map",
      error: false,
      note: null,
      sheet: null,
      scheduleBusy: false
    };
    this._alive = true;
  }
  range() {
    return this.props.params && this.props.params.range || getGlobalRange();
  }
  tab() {
    return this.props.params && this.props.params.tab || "overview";
  }
  path(range, tab) {
    return `/Sales/${range || this.range()}${tab && tab !== "overview" ? `/${tab}` : ""}`;
  }
  componentDidMount() {
    if (!(this.props.params && this.props.params.range)) {
      this.props.history.replace(this.path(getGlobalRange(), "overview"));
      return;
    }
    this.boot();
    this._periodTok = subscribePeriod(({
      range,
      comp
    }) => {
      if (!this._alive) return;
      if (comp && comp !== this.state.comp) this.setState({
        comp
      });
      const target = this.path(range, this.tab());
      if (window.location.pathname === target) this.reset(() => this.load());else this.props.history.push(target);
    });
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    const p = prev.params || {};
    const c = this.props.params || {};
    if (!p.range && c.range) {
      this.componentDidMount();
      return;
    }
    if (p.range !== c.range) this.reset(() => this.load());else if (p.tab !== c.tab && c.tab === "coverage" && !this.state.geoLoaded) this.loadGeo();
  }
  async boot() {
    const bm = await resolveBusinessModel().catch(() => "both");
    if (!this._alive) return;
    this.setState({
      bm
    }, () => this.load());
  }
  reset(cb) {
    this.setState({
      failed: null,
      dlMatrix: undefined,
      overview: undefined,
      reps: undefined,
      dealers: undefined,
      stores: undefined,
      coverage: undefined,
      channels: null,
      gaps: undefined,
      geoSummary: undefined,
      geoCoverage: undefined,
      geoDealers: undefined,
      geoHeat: undefined,
      geoStores: undefined,
      geoLoaded: false,
      error: false
    }, cb);
  }
  query() {
    const {
      bm,
      channel,
      status,
      comp
    } = this.state;
    const range = this.range();
    return {
      comparison: range === "all_time" ? undefined : apiComparison(comp),
      customerType: bm === "both" && channel ? channel : undefined,
      status: status && status.length ? status.join(",") : undefined
    };
  }
  isRetail() {
    const {
      bm,
      channel
    } = this.state;
    return bm === "retail" || bm === "both" && channel === "retail";
  }
  load() {
    const range = this.range();
    const q = this.query();
    const set = patch => this._alive && this.setState(patch);
    // A rail that fails hard degrades to its empty state and is named in a notice; the page itself stays up.
    const fail = key => e => {
      if (!this._alive) return;
      if (!isSoft(e)) {
        const msg = e && (e.message || e.body && e.body.error) || "did not answer";
        try {
          console.warn("[cockpit] " + key + " failed: " + msg, e);
        } catch (x) {/* no console */}
        this.setState(st => ({
          failed: {
            ...(st.failed || {}),
            [key]: String(msg).slice(0, 160)
          }
        }));
      }
      set({
        [key]: null
      });
    };
    // The four cockpit rails share one per-process facts cache on the server. Fired in parallel on a cold
    // cache they land on four fresh Lambda environments that each rebuild the window from scratch and
    // contend for the database (LEE: ~30 s each, past the gateway's limit). Chained, only the first call
    // pays and the rest reuse the warm environment, so the page fills in a few seconds.
    const retail = this.isRetail();
    set(retail ? {
      dealers: null
    } : {
      stores: null
    });
    const step = (key, run) => () => run().then(v => set({
      [key]: v
    })).catch(fail(key));
    step("overview", () => getOverviewOrTotals(range, q))().then(step("reps", () => getRepScorecardsOrRoster(range, {
      ...q,
      sort: "attainment",
      sparkline: 12
    }))).then(retail ? step("stores", () => getStoreScorecardsOrRoster(range, {
      ...q,
      sparkline: 12
    })) : step("dealers", () => getDealerScorecardsOrRoster(range, {
      ...q,
      sort: "band",
      top: 2000,
      sparkline: 12
    }))).then(step("coverage", () => getCoverageOrTerritories(range, q))).then(() => {
      if (!retail) getDealerPerformance(range, {
        $groupBy: "category"
      }).then(m => set({
        dlMatrix: m && m.items || []
      })).catch(() => set({
        dlMatrix: null
      }));
    });
    // Demand gaps (styles viewed far more than ordered): manufacturing and retail alike; fail-soft, the card hides on null.
    getDemandGaps(range).then(gaps => set({
      gaps
    })).catch(() => set({
      gaps: null
    }));
    getGeoHeat(range, "sessions", this.isRetail() ? "retail" : undefined).then(geoHeat => set({
      geoHeat
    })).catch(() => set({
      geoHeat: null
    }));
    // Channel split: the overview carries it; otherwise the channels rollup that exists.
    fetchSales(`/sales/channels/${range}${q.customerType ? `?customerType=${q.customerType}` : ""}`).then(rows => set({
      channels: Array.isArray(rows) ? rows : []
    })).catch(() => set({
      channels: []
    }));
    if (this.tab() === "coverage") this.loadGeo();
  }
  loadGeo() {
    if (this.state.geoLoaded) return;
    const range = this.range();
    const q = this.query();
    const set = patch => this._alive && this.setState(patch);
    this.setState({
      geoLoaded: true
    });
    getGeoSummary(range, q).then(geoSummary => set({
      geoSummary
    })).catch(() => set({
      geoSummary: null
    }));
    getGeoCoverage({
      channel: q.customerType
    }).then(geoCoverage => set({
      geoCoverage
    })).catch(() => set({
      geoCoverage: null
    }));
    getGeoDealers(range, {
      channel: q.customerType
    }).then(geoDealers => set({
      geoDealers
    })).catch(() => set({
      geoDealers: null
    }));
    getGeoHeat(range, "sessions", this.isRetail() ? "retail" : undefined).then(geoHeat => set({
      geoHeat
    })).catch(() => set({
      geoHeat: null
    }));
    getGeoStores(range).then(geoStores => set({
      geoStores
    })).catch(() => set({
      geoStores: null
    }));
    if (this.isRetail()) this.loadMiMarkets();
  }
  // Retail: the tenant's Market Intelligence markets with the ZIP3s they cover, so a market row opens its MI page.
  loadMiMarkets() {
    if (this.state.miMarkets !== undefined) return;
    this.setState({
      miMarkets: null
    });
    MI.getLocations().then(raw => {
      const list = raw && (raw.locations || raw.items) || (Array.isArray(raw) ? raw : []);
      const byZip3 = {};
      list.forEach(m => (m.zip3s || []).forEach(z => {
        if (!byZip3[z]) byZip3[z] = m.name;
      }));
      this._alive && this.setState({
        miMarkets: byZip3
      });
    }).catch(() => this._alive && this.setState({
      miMarkets: {}
    }));
  }
  miMarketFor(z) {
    const m = this.state.miMarkets;
    return m && z && m[z.zip3] || null;
  }
  // ---- helpers over the loaded envelopes ----
  repRows() {
    const env = this.state.reps;
    if (!env) return [];
    const {
      repChannel,
      repRegion,
      repStatus,
      repOnPlan,
      repSort
    } = this.state;
    let rows = env.items.slice();
    if (repChannel) rows = rows.filter(r => String(r.channel || "").toLowerCase() === repChannel.toLowerCase());
    if (repRegion) rows = rows.filter(r => r.states.includes(repRegion));
    if (repStatus === "active") rows = rows.filter(isRosterRep);else if (repStatus) rows = rows.filter(r => r.status === repStatus);
    if (repOnPlan === "yes") rows = rows.filter(r => r.pace != null && r.pace >= 1);
    if (repOnPlan === "no") rows = rows.filter(r => r.pace != null && r.pace < 1);
    const key = env.hasTargets ? repSort : repSort === "attainment" || repSort === "pace" ? "revenue" : repSort;
    const v = r => key === "atRisk" ? r.health && r.health.atRisk + r.health.lapsing || 0 : Number(r[key]) || 0;
    rows.sort((a, b) => v(b) - v(a));
    return rows;
  }
  dealerRows() {
    const env = this.state.dealers;
    if (!env) return [];
    const {
      dlRep,
      dlRegion,
      dlChannel,
      dlBand,
      dlStage,
      dlSort
    } = this.state;
    let rows = env.items.slice();
    if (dlRep) rows = rows.filter(d => d.assignedRep && d.assignedRep.repCode === dlRep);
    if (dlRegion) rows = rows.filter(d => d.region === dlRegion);
    if (dlChannel) rows = rows.filter(d => String(d.channel || "").toLowerCase() === dlChannel.toLowerCase());
    if (dlBand) {
      const bands = dlBand.split(",");
      rows = rows.filter(d => d.health && bands.includes(d.health.band));
    }
    if (dlStage) rows = rows.filter(d => d.stage === dlStage);
    if (this.state.dlNew) rows = rows.filter(d => d.raw && d.raw.newDealer);
    if (this.state.dlFewer) rows = rows.filter(d => this.fewerOrders(d));
    if (this.state.dlOther) rows = rows.filter(d => (Number(d.sellingReps) || 0) > 1 || !d.assignedRep && (Number(d.sellingReps) || 0) > 0);
    const key = env.hasTargets ? dlSort : dlSort === "attainment" || dlSort === "pace" ? "revenue" : dlSort;
    if (key === "band") rows.sort((a, b) => BAND_RANK[a.health && a.health.band || "healthy"] - BAND_RANK[b.health && b.health.band || "healthy"] || b.revenue - a.revenue);else if (key === "lastOrder") rows.sort((a, b) => a.lastOrder < b.lastOrder ? 1 : -1);else rows.sort((a, b) => (Number(b[key]) || 0) - (Number(a[key]) || 0));
    return rows;
  }
  // Fewer orders than the same dates last period (paced for an in-progress window).
  fewerOrders(d) {
    if (!d.prev || !(Number(d.prev.orders) > 0)) return false;
    const x = deltaOfPaced(d.orders, d.prev.orders, this.range());
    return !!(x && x.dir === "down");
  }
  // Open the Dealers tab with one filter set (the strip's "See ..." links).
  goDealers(patch) {
    this.setState({
      dlRep: "",
      dlRegion: "",
      dlChannel: "",
      dlBand: "",
      dlStage: "",
      dlNew: false,
      dlFewer: false,
      dlOther: false,
      ...patch
    });
    this.props.history.push(this.path(this.range(), "dealers"));
    try {
      window.scrollTo(0, 0);
    } catch (e) {/* no window */}
  }
  bandCounts() {
    const env = this.state.dealers;
    if (!env) return null;
    if (env.bands) return env.bands;
    const c = {
      healthy: 0,
      watch: 0,
      at_risk: 0,
      lapsing: 0
    };
    let any = false;
    env.items.forEach(d => {
      const b = d.health && d.health.band;
      if (b && b in c) {
        c[b]++;
        any = true;
      }
    });
    return any ? c : null;
  }
  coverageRows() {
    const cov = this.state.coverage;
    if (!cov) return [];
    const sev = s => s.flags.includes("uncovered") ? 0 : s.flags.includes("overlap") ? 1 : s.flags.includes("no activity") ? 2 : 3;
    return cov.states.slice().sort((a, b) => sev(a) - sev(b) || b.revenue - a.revenue);
  }

  // ---- render ----
  render() {
    if (!hasTenantFeature("salesTransactions") && !hasTenantFeature("territoryRep")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "The Sales cockpit is not configured for this workspace. It requires a sales feed (orders, and for manufacturers the rep and dealer feeds)."));
    }
    if (!isSalesUser()) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "The Sales cockpit is available to workspace owners, administrators and members of the sales group. Ask an administrator to add you in Manage Users."));
    }
    const {
      bm,
      error,
      overview,
      note,
      sheet
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!bm) return /*#__PURE__*/React.createElement(Loader, null);
    const range = this.range();
    const tab = this.tab();
    const retail = this.isRetail();
    const inProgress = isInProgressRange(range);
    const admin = isAdminUser();
    const repsEnv = this.state.reps;
    const showReps = !retail; // retail has stores and customers, no reps
    const dealersLabel = retail ? "Stores" : "Dealers";
    const tabs = [{
      to: this.path(range, "overview"),
      label: "Overview",
      exact: true,
      isActive: p => p === this.path(range, "overview")
    }, showReps ? {
      to: this.path(range, "reps"),
      label: "Reps"
    } : null, {
      to: this.path(range, "dealers"),
      label: dealersLabel
    }, {
      to: this.path(range, "coverage"),
      label: retail ? "Markets" : "Coverage"
    }, admin ? {
      to: retail ? "/Stores/targets" : "/Sales/targets",
      label: "Targets"
    } : null].filter(Boolean);
    const notes = overview && overview.dataNotes || [];
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view sales-view sales-exec"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, "Sales \xB7 Cockpit"),
      title: "Sales",
      subtitle: `${rangeLabelFor(range, RANGES)} · ${retail ? "How your stores are doing, and where to act" : "How your reps and dealers are doing, and where to act"}${range === "all_time" ? "" : ` · changes are against the same dates ${this.state.comp === "prev_year" ? "last year" : "last period"}`}`,
      chips: [...notes.map((t, i) => ({
        k: "Data note",
        v: t,
        title: t
      }))],
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, bm === "both" && /*#__PURE__*/React.createElement(PlunkDropDown, {
        width: "150px",
        value: this.state.channel,
        onChange: this.onChannel,
        options: [["", "All channels"], ["wholesale", "Wholesale"], ["retail", "Retail"]]
      }), /*#__PURE__*/React.createElement(StatusFilter, {
        range: range,
        value: this.state.status,
        onChange: this.onStatus
      }), /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: this.exportCsv,
        title: "Download this view as CSV"
      }, "Export"), /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        disabled: this.state.scheduleBusy,
        onClick: this.openSchedule,
        title: "Email this cockpit on a schedule"
      }, "Schedule"))
    }), /*#__PURE__*/React.createElement(Switcher, {
      items: tabs,
      ariaLabel: "Sales cockpit views"
    }), note ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, note, " ", /*#__PURE__*/React.createElement("a", {
      className: "wa-rowlink",
      style: {
        marginLeft: 8
      },
      onClick: () => this.setState({
        note: null
      })
    }, "dismiss")) : null, this.renderRibbon(retail, inProgress), this.state.failed && Object.keys(this.state.failed).length ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice",
      title: "The named rails returned an error; everything else on the page is live"
    }, Object.entries(this.state.failed).map(([k, m]) => `${k}: ${m}`).join(" · "), " ", /*#__PURE__*/React.createElement("a", {
      className: "wa-rowlink",
      onClick: () => this.load()
    }, "Try again")) : null, tab === "overview" && this.renderOverview(retail, inProgress), tab === "reps" && this.renderReps(inProgress), tab === "dealers" && (retail ? this.renderStores(inProgress) : this.renderDealers(inProgress)), tab === "coverage" && (retail ? this.renderMarkets() : this.renderCoverage(retail)), sheet && /*#__PURE__*/React.createElement(ScheduleSheet, {
      report: sheet.report,
      schedule: sheet.schedule,
      onClose: () => this.setState({
        sheet: null
      }),
      onChanged: () => {}
    }));
  }

  // Areas (ZIP3) where the website sees demand and no rep is assigned: browsing, nobody selling.
  uncoveredDemand(limit) {
    const heat = this.state.geoHeat;
    if (!heat || !heat.length) return [];
    return heat.filter(z => z.uncovered && (z.demand > 0 || z.demandIndex > 0)).sort((a, b) => (b.demand || b.demandIndex) - (a.demand || a.demandIndex) || b.dealers - a.dealers).slice(0, limit || 25);
  }
  placeLabel(z) {
    return `${String(z.label || z.zip3 || "").replace(/\s*\(\d{3}\)\s*$/, "").replace(/ area$/i, "")}${z.state ? `, ${z.state}` : ""}`;
  }

  // The cockpit strip (mockup sales-forecasts-inventory-redesign-2026-10-09, board 1): two rows of four tiles,
  // plain labels, each tile saying what to do next. A tile with nothing to show says why instead of a dash.
  renderRibbon(retail, inProgress) {
    const {
      overview,
      reps,
      dealers,
      stores,
      coverage,
      geoHeat
    } = this.state;
    const range = this.range();
    const tab = this.tab();
    if (tab === "coverage") return null; // the Coverage tab has its own strip
    if (overview === undefined) return /*#__PURE__*/React.createElement(Ribbon, {
      loading: true,
      tiles: []
    });
    const o = overview || {};
    const repsEnv = reps || null;
    const bookEnv = retail ? stores : dealers;
    const win = inWindow(range);
    const admin = isAdminUser();
    const targetsPath = retail ? "/Stores/targets" : "/Sales/targets";
    const pending = env => env === undefined;
    // Slowing down = overdue + stopped ordering (each dealer against how often it usually orders).
    const bands = bookEnv && (bookEnv.bands || (() => {
      const c = {
        at: 0,
        rev: 0
      };
      let any = false;
      bookEnv.items.forEach(d => {
        const b = d.health && d.health.band;
        if (b === "at_risk" || b === "lapsing") {
          c.at++;
          c.rev += d.health.revenueAtStake || 0;
          any = true;
        } else if (b) any = true;
      });
      return any ? {
        at_risk: c.at,
        lapsing: 0,
        revenue: c.rev
      } : null;
    })());
    const slowing = o.dealersAtRisk || (bands ? {
      count: (bands.at_risk || 0) + (bands.lapsing || 0),
      revenue: bookEnv && bookEnv.revenueAtRisk || bands.revenue || 0
    } : null);
    const unc = o.uncoveredStates || (coverage ? {
      count: coverage.summary.uncovered,
      revenue: coverage.summary.uncoveredRevenue
    } : null);
    const hasTargets = !!(o.hasTargets && (o.target || o.attainment != null || o.pace != null)) || !!(repsEnv && repsEnv.hasTargets) || !!(bookEnv && bookEnv.hasTargets);
    const pace = o.pace != null ? o.pace : repsEnv && repsEnv.totals ? Number(repsEnv.totals.pace) : null;
    const att = o.attainment != null ? o.attainment : repsEnv && repsEnv.totals ? Number(repsEnv.totals.attainment) : null;
    const onPlan = o.repsOnPlan || (repsEnv && repsEnv.hasTargets ? {
      on: repsEnv.items.filter(r => r.pace != null && r.pace >= 1).length,
      of: repsEnv.items.filter(r => r.target).length
    } : null);
    const revD = range === "all_time" ? null : kitDelta(deltaOfPaced(o.revenue, o.prevRevenue, range));
    const hot = this.uncoveredDemand(1)[0];
    const oot = o.outOfTerritoryPct;
    const perOrderRep = o.hasPerOrderRepData !== false;
    const newCount = o.newDealers;
    const tiles = [{
      l: "Revenue",
      t: "Everything booked in this period, cancelled orders left out; the change is against the same dates last period.",
      v: usdK(o.revenue),
      d: revD,
      noDelta: !revD,
      s: o.orders ? `${num(o.orders)} orders · ${usd0(o.revenue / o.orders)} average` : "no orders in this period"
    }, {
      l: retail ? "Customers buying" : "Dealers buying",
      t: retail ? "Customers who placed at least one order in this period." : "Dealers who placed at least one order in this period.",
      v: num(o.activeDealers),
      noDelta: true,
      s: retail ? "" : newCount != null ? tileSub(`${num(newCount)} ordered for the first time ${win}`, newCount > 0 ? /*#__PURE__*/React.createElement(Go, {
        onClick: () => this.goDealers({
          dlNew: true,
          dlSort: "revenue"
        })
      }, "See new dealers") : null) : "first orders show once the dealer feed is in"
    }, {
      l: retail ? "Customers slowing down" : "Dealers slowing down",
      t: `${retail ? "Customers" : "Dealers"} ordering later than they usually do, each compared with their own usual time between orders.`,
      noDelta: true,
      v: slowing ? num(slowing.count) : pending(bookEnv) ? "…" : "Not yet",
      s: slowing ? tileSub(`ordering later than they usually do · ${usdK(slowing.revenue)} a year`, slowing.count > 0 && !retail ? /*#__PURE__*/React.createElement(Go, {
        onClick: () => this.goDealers({
          dlBand: "at_risk,lapsing",
          dlSort: "band"
        })
      }, "See who to call") : null) : pending(bookEnv) ? "" : `shows once ${retail ? "customers" : "dealers"} have two or more orders`
    }, hasTargets ? {
      l: "Against plan",
      t: "Revenue so far against the plan for the same share of the period.",
      noDelta: true,
      v: att != null ? pct0(att) : "Not yet",
      s: tileSub(pace != null ? `${inProgress ? "on course for " : ""}${pct0(pace)} of plan${inProgress ? ` · ${elapsedLabel(range)}` : ""}` : "of plan", admin ? /*#__PURE__*/React.createElement(Go, {
        to: targetsPath
      }, "See targets") : null)
    } : {
      l: "Against plan",
      t: "Revenue against the yearly goals set on Targets.",
      noDelta: true,
      v: "No plan yet",
      s: tileSub("set yearly goals to track reps against them", admin ? /*#__PURE__*/React.createElement(Go, {
        to: targetsPath
      }, "Set targets") : null)
    }, !retail ? {
      l: "States with no rep",
      t: "States where you have dealers or sales and no rep is assigned.",
      noDelta: true,
      v: unc ? num(unc.count) : coverage === undefined ? "…" : "Not yet",
      s: unc ? tileSub(unc.count ? `${usdK(unc.revenue)} sold there ${win}` : "every state with dealers has a rep", unc.count ? /*#__PURE__*/React.createElement(Go, {
        to: this.path(range, "coverage")
      }, "See coverage") : null) : coverage === undefined ? "" : "shows once territories are loaded"
    } : null, !retail ? {
      l: "Sold by another rep",
      t: "Share of orders sold by a rep who is not the dealer's assigned rep.",
      noDelta: true,
      v: perOrderRep && oot != null ? pct0(oot) : "Not tracked",
      s: perOrderRep && oot != null ? tileSub("of orders were sold by a rep who isn't the dealer's own", oot > 0 ? /*#__PURE__*/React.createElement(Go, {
        onClick: () => this.goDealers({
          dlOther: true,
          dlSort: "revenue"
        })
      }, "See which dealers") : null) : "needs the selling rep on each order"
    } : null, !retail ? {
      l: "Reps on plan",
      t: "Reps on course to meet their goal for this period.",
      noDelta: true,
      v: hasTargets && onPlan ? `${num(onPlan.on)} of ${num(onPlan.of)}` : "Not yet",
      s: hasTargets && onPlan ? tileSub("on course for their goal", /*#__PURE__*/React.createElement(Go, {
        to: this.path(range, "reps")
      }, "See reps")) : "shows once targets are set"
    } : null, !retail ? {
      l: "Website interest, no rep",
      t: "The area with the most website visits where no rep is assigned.",
      noDelta: true,
      v: hot ? this.placeLabel(hot) : geoHeat === undefined ? "…" : "None",
      s: hot ? tileSub(`${num(hot.demand || hot.demandIndex)} visits ${win} in an area with no rep`, /*#__PURE__*/React.createElement(Go, {
        to: this.path(range, "coverage")
      }, "See areas")) : geoHeat === undefined ? "" : geoHeat === null ? "shows once the website tracker is on" : "every area with visits has a rep"
    } : null].filter(Boolean);
    const note = overview === null ? "The overview is not available right now; the strip reads the sales totals." : o.source === "roster" ? "Reading the sales totals: targets and new dealers fill in shortly." : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "sc-strip"
    }, /*#__PURE__*/React.createElement(Ribbon, {
      comparing: range === "all_time" ? null : true,
      cols: 4,
      tiles: tiles,
      note: note
    }));
  }
  renderOverview(retail, inProgress) {
    const {
      reps,
      dealers,
      stores,
      coverage,
      overview,
      channels
    } = this.state;
    const range = this.range();
    const repsEnv = reps;
    const bookEnv = retail ? stores : dealers;
    // The house account (house / no rep) is not a rep: it is not ranked, it gets its own line under the list.
    const activeReps = repsEnv ? repsEnv.items.filter(r => r.status !== "terminated") : [];
    const houseReps = activeReps.filter(isHouseRep).filter(r => r.revenue);
    const rankedReps = activeReps.filter(r => !isHouseRep(r)).sort((a, b) => repsEnv.hasTargets ? (b.attainment || 0) - (a.attainment || 0) : b.revenue - a.revenue);
    const topBottom = rankedReps.length > 6 ? [...rankedReps.slice(0, 3), ...rankedReps.slice(-3)] : rankedReps;
    const isSlow = d => d.health && (d.health.band === "at_risk" || d.health.band === "lapsing");
    let slipping = bookEnv ? bookEnv.items.filter(isSlow).sort((a, b) => BAND_RANK[a.health.band] - BAND_RANK[b.health.band] || (b.health.revenueAtStake || b.revenue) - (a.health.revenueAtStake || a.revenue)).slice(0, 8) : [];
    // One count with the "Dealers slowing down" tile: the overview's whole-book list when the scorecards hold none
    // (an older API returned only dealers with orders in the window, and a slowing dealer usually has none).
    const ovSlow = !retail && overview && overview.dealersSlipping || [];
    if (!slipping.length && ovSlow.length) slipping = ovSlow.filter(isSlow).slice(0, 8);
    const slowCount = !retail && overview && overview.dealersAtRisk ? overview.dealersAtRisk.count : bookEnv ? bookEnv.items.filter(isSlow).length : 0;
    const slowRevenue = !retail && overview && overview.dealersAtRisk && overview.dealersAtRisk.revenue ? overview.dealersAtRisk.revenue : bookEnv && bookEnv.revenueAtRisk || slipping.reduce((a, d) => a + (d.health.revenueAtStake || 0), 0);
    const anyBands = bookEnv ? bookEnv.items.some(d => d.health && d.health.band) : false;
    const gaps = coverage ? coverage.states.filter(s => s.flags.length).slice(0, 8) : [];
    const tvp = overview && overview.trendVsPlan || [];
    const chRows = overview && overview.channels && overview.channels.length ? overview.channels : (channels || []).filter(c => String(c.channel || "").toLowerCase() !== "unsegmented").length ? (channels || []).map(c => ({
      channel: c.channel,
      revenue: Number(c.totalAmount) || 0,
      reps: Number(c.reps) || 0
    })) : [];
    // Sales by channel stays hidden until at least one rep has a channel set (every rep Unsegmented = nothing to split).
    const anyChannel = chRows.some(c => c.channel && String(c.channel).toLowerCase() !== "unsegmented");
    const chTotal = chRows.reduce((a, c) => a + c.revenue, 0);
    const pace = overview && overview.pace;
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, !retail && /*#__PURE__*/React.createElement(Card, {
      title: "Top reps",
      hint: repsEnv && repsEnv.hasTargets ? "by progress to goal" : "by revenue; shows progress to goal once targets are set",
      span: 4,
      right: /*#__PURE__*/React.createElement(Link, {
        className: "wa-rowlink",
        to: this.path(range, "reps")
      }, "Reps tab")
    }, repsEnv === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 140
    }) : !topBottom.length ? /*#__PURE__*/React.createElement(Empty, null, repsEnv === null ? "The rep rail is not available for this workspace yet." : "No reps on the roster. Reps arrive from the sales rep feed (Manage Data, Connect).") : /*#__PURE__*/React.createElement("table", {
      className: "territory-table sx-mini"
    }, /*#__PURE__*/React.createElement("tbody", null, topBottom.map((r, i) => /*#__PURE__*/React.createElement("tr", {
      key: r.repCode,
      className: "clickable",
      onClick: () => this.openRep(r.repCode)
    }, /*#__PURE__*/React.createElement("td", null, rankedReps.length > 6 && i === 3 ? /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "Lowest ", inWindow(range)) : null, rankedReps.length > 6 && i === 3 ? /*#__PURE__*/React.createElement("br", null) : null, /*#__PURE__*/React.createElement("b", null, r.name), " ", /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, r.repCode)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, usdK(r.revenue)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, repsEnv.hasTargets ? /*#__PURE__*/React.createElement(AttainBar, {
      ratio: r.attainment,
      tone: paceTone(r.pace)
    }) : null), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, (() => {
      const d = kitDelta(deltaOfPaced(r.revenue, r.prev.revenue, range));
      return d ? /*#__PURE__*/React.createElement(Delta, {
        d: d
      }) : r.revenue ? /*#__PURE__*/React.createElement("span", {
        className: "hint"
      }, "new") : /*#__PURE__*/React.createElement("span", {
        className: "hint"
      }, "no sales");
    })()))))), houseReps.length ? /*#__PURE__*/React.createElement("p", {
      className: "wa-foot sc-house",
      title: "Orders booked to the house account (no rep) are not ranked with the reps"
    }, /*#__PURE__*/React.createElement("b", null, "House account"), " ", houseReps.map(r => `${r.name} ${usdK(r.revenue)}`).join(" · "), " ", /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "\xB7 not ranked with reps")) : null), /*#__PURE__*/React.createElement(Card, {
      title: retail ? "Stores needing attention" : "Dealers slowing down",
      hint: retail ? "each store compared with how often its customers usually order" : "each dealer compared with how often it usually orders",
      span: 4,
      right: /*#__PURE__*/React.createElement(Link, {
        className: "wa-rowlink",
        to: this.path(range, "dealers")
      }, retail ? "Stores" : "Dealers", " tab")
    }, bookEnv === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 140
    }) : !slipping.length ? /*#__PURE__*/React.createElement(Empty, null, bookEnv === null ? `${retail ? "Store" : "Dealer"} health is not available for this workspace yet.` : slowCount > 0 ? `${num(slowCount)} ${retail ? "stores are" : "dealers are"} ordering later than usual. The ${retail ? "Stores" : "Dealers"} tab lists them.` : anyBands ? `Every ${retail ? "store" : "dealer"} is ordering about as often as usual.` : retail ? "Shows once stores have enough order history to compare." : "Shows once dealers have two or more orders on file.") : /*#__PURE__*/React.createElement("table", {
      className: "territory-table sx-mini"
    }, /*#__PURE__*/React.createElement("tbody", null, slipping.map(d => /*#__PURE__*/React.createElement("tr", {
      key: d.id,
      className: "clickable",
      onClick: () => retail ? this.openStore(d.id) : this.openDealer(d.accountNumber || d.id)
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, d.name), " ", /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, d.assignedRep ? d.assignedRep.name : ""), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "Ordering later than usual", lastOrderText(d.health) ? ` · ${lastOrderText(d.health)}` : "")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement(Pill, {
      tone: lapseTone(d.health.band)
    }, lapseLabel(d.health.band))), /*#__PURE__*/React.createElement("td", {
      className: "n",
      title: "What this dealer bought in the last twelve months"
    }, usdK(d.health.revenueAtStake || d.revenue), /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, " a year")))))), bookEnv && slipping.length ? /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, num(Math.max(slowCount, slipping.length)), " ", retail ? "stores" : "dealers", " slowing down \xB7 ", usdK(slowRevenue), " a year at stake") : null), retail ? this.renderTopMarkets() : null, !retail && /*#__PURE__*/React.createElement(Card, {
      title: "States to look at",
      hint: "no rep assigned, or shared by two reps",
      span: 4,
      right: /*#__PURE__*/React.createElement(Link, {
        className: "wa-rowlink",
        to: this.path(range, "coverage")
      }, "Coverage tab")
    }, coverage === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 140
    }) : !gaps.length ? /*#__PURE__*/React.createElement(Empty, null, coverage === null ? "Coverage is not available for this workspace yet." : coverage.states.length ? "Every state with dealers has one rep." : "No territories yet. Upload ZIP or state assignments in Manage Data, Territories.") : /*#__PURE__*/React.createElement("table", {
      className: "territory-table sx-mini"
    }, /*#__PURE__*/React.createElement("tbody", null, gaps.map(s => /*#__PURE__*/React.createElement("tr", {
      key: s.region,
      className: "clickable",
      onClick: () => this.openRegion(s.region)
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, s.region), " ", /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, num(s.dealers), " dealers")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, usdK(s.revenue)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sx-chips"
    }, s.flags.map(f => /*#__PURE__*/React.createElement(Pill, {
      key: f,
      tone: f === "uncovered" ? "danger" : f === "overlap" ? "warning" : "neutral"
    }, flagLabel(f)))))))))), !retail && this.state.geoHeat !== null ? /*#__PURE__*/React.createElement(Card, {
      title: "Website interest with no rep",
      hint: "areas people browse from where no rep is assigned",
      span: 4,
      right: /*#__PURE__*/React.createElement(Link, {
        className: "wa-rowlink",
        to: this.path(range, "coverage")
      }, "Coverage tab")
    }, this.state.geoHeat === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 140
    }) : !this.uncoveredDemand(5).length ? /*#__PURE__*/React.createElement(Empty, null, "Every area with website visits has a rep.") : /*#__PURE__*/React.createElement("table", {
      className: "territory-table sx-mini"
    }, /*#__PURE__*/React.createElement("tbody", null, this.uncoveredDemand(5).map(z => /*#__PURE__*/React.createElement("tr", {
      key: z.zip3,
      className: z.state ? "clickable" : "",
      onClick: () => z.state && this.openRegion(z.state)
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, this.placeLabel(z)), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, "ZIP ", z.zip3)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(z.demand || z.demandIndex), " ", /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "visits")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, z.dealers ? /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, num(z.dealers), " ", Number(z.dealers) === 1 ? "dealer" : "dealers") : /*#__PURE__*/React.createElement(Pill, {
      tone: "danger"
    }, "no dealer"))))))) : null, this.renderDemandGaps(range), /*#__PURE__*/React.createElement(Card, {
      title: "Sales by month",
      hint: "the dashed line is your plan once targets are set",
      span: 4,
      right: pace != null ? /*#__PURE__*/React.createElement(Pill, {
        tone: pace >= 1 ? "success" : pace >= 0.9 ? "warning" : "danger"
      }, "Pace ", pct0(pace)) : null
    }, overview === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 140
    }) : !tvp.length ? /*#__PURE__*/React.createElement(Empty, null, overview && overview.hasTargets ? "Monthly sales are not available for this period yet." : isAdminUser() ? /*#__PURE__*/React.createElement(React.Fragment, null, "No plan yet. ", /*#__PURE__*/React.createElement(Link, {
      to: this.isRetail() ? "/Stores/targets" : "/Sales/targets"
    }, "Set targets"), " to see sales against plan.") : "No plan set for this period.") : this.renderTrendVsPlan(tvp)), !retail && anyChannel && /*#__PURE__*/React.createElement(Card, {
      title: "Sales by channel",
      hint: "by the selling rep's channel",
      span: 4,
      right: chRows.length ? /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: () => this.openChannel(chRows[0].channel)
      }, "Channel detail") : null
    }, channels === null && !(overview && overview.channels && overview.channels.length) ? /*#__PURE__*/React.createElement(Pending, {
      h: 120
    }) : !chRows.length ? /*#__PURE__*/React.createElement(Empty, null, "No channel tags on the rep roster. Reps without a tag roll up as Unsegmented.") : /*#__PURE__*/React.createElement("table", {
      className: "territory-table sx-mini"
    }, /*#__PURE__*/React.createElement("tbody", null, chRows.slice().sort((a, b) => b.revenue - a.revenue).map(c => /*#__PURE__*/React.createElement("tr", {
      key: c.channel,
      className: "clickable",
      onClick: () => this.openChannel(c.channel)
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, channelLabel(c.channel)), " ", c.reps ? /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, num(c.reps), " reps") : null), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, usdK(c.revenue)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement(AttainBar, {
      ratio: chTotal ? c.revenue / chTotal : 0,
      tone: ""
    }))))))));
  }

  // Overview card: top 5 demand gaps (sorted by the rail's gap when it carries one, else the rail's order).
  // Hidden when the rail answers null or the list is empty. Sits beside "Website interest with no rep" (both span 4).
  renderDemandGaps(range) {
    const env = this.state.gaps;
    if (env === null || env && !env.items.length) return null;
    const trade = env && env.siteAudience === "trade";
    const rows = env ? env.items.slice(0, 5) : [];
    return /*#__PURE__*/React.createElement(Card, {
      title: "Looked at more than ordered",
      hint: trade ? "products people view a lot but dealers rarely order" : "products people view a lot but rarely buy",
      span: 4,
      right: /*#__PURE__*/React.createElement(Link, {
        className: "wa-rowlink",
        to: `/Products/${range}?grain=pricing`,
        title: "Open the full list in Product Explorer"
      }, "See all")
    }, env === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 140
    }) : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table sx-mini"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      title: "Click a product for its detail"
    }, "Product"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Product page views in this period"
    }, "Views"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: trade ? "Units dealers ordered in this period" : "Units bought in this period"
    }, "Units ordered"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(r => /*#__PURE__*/React.createElement("tr", {
      key: r.productId,
      className: "clickable",
      title: r.reason || undefined,
      onClick: () => this.props.history.push(`/Products/${range}/product/${encodeURIComponent(r.productId)}`)
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, r.title || r.productId), " ", r.title ? /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, r.productId) : null), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(r.views)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(r.units))))))));
  }
  renderTrendVsPlan(tvp) {
    const W = 320,
      H = 90,
      L = 12,
      R = 12,
      B = 12;
    const mx = Math.max(1, ...tvp.map(p => Math.max(p.actual || 0, p.plan || 0))) * 1.05;
    const n = tvp.length;
    const X = i => L + (n > 1 ? i * (W - L - R) / (n - 1) : 0);
    const Y = v => H - B - (v || 0) / mx * (H - B - 8);
    const actual = tvp.filter(p => p.actual > 0);
    const planPts = tvp.filter(p => p.plan != null).map(p => `${X(tvp.indexOf(p)).toFixed(1)},${Y(p.plan).toFixed(1)}`).join(" ");
    const actPts = tvp.map((p, i) => p.actual > 0 || i < actual.length ? `${X(i).toFixed(1)},${Y(p.actual).toFixed(1)}` : null).filter(Boolean).join(" ");
    return /*#__PURE__*/React.createElement("svg", {
      viewBox: `0 0 ${W} ${H}`,
      style: {
        width: "100%",
        height: "auto"
      },
      role: "img",
      "aria-label": "Monthly revenue against plan"
    }, planPts ? /*#__PURE__*/React.createElement("polyline", {
      points: planPts,
      fill: "none",
      stroke: "var(--plk-chart-previous)",
      strokeWidth: "2",
      strokeDasharray: "4 3"
    }) : null, actPts ? /*#__PURE__*/React.createElement("polyline", {
      points: actPts,
      fill: "none",
      stroke: "var(--plk-chart-current)",
      strokeWidth: "2.2"
    }) : null, tvp.map((p, i) => /*#__PURE__*/React.createElement("text", {
      key: i,
      x: X(i).toFixed(1),
      y: H - 1,
      fontSize: "8",
      textAnchor: "middle",
      fill: "var(--plk-text-subtle)"
    }, String(p.period).length >= 7 ? "JFMAMJJASOND"[Number(String(p.period).slice(5, 7)) - 1] || "" : String(p.period).slice(0, 1))));
  }
  renderFilters(children, right) {
    return /*#__PURE__*/React.createElement("div", {
      className: "sx-filters"
    }, children, /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), right);
  }
  renderReps(inProgress) {
    const env = this.state.reps;
    const range = this.range();
    if (env === undefined) return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Pending, {
      h: 240
    })));
    if (env === null) return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Rep scorecards"
    }, /*#__PURE__*/React.createElement(Empty, null, "The rep rail is not available for this workspace yet. Rep scorecards appear once the sales rep feed and the scorecard rail are live.")));
    const rows = this.repRows();
    const {
      repChannel,
      repRegion,
      repStatus,
      repOnPlan,
      repSort
    } = this.state;
    const channels = [...new Set(env.items.map(r => r.channel).filter(Boolean))];
    const regions = [...new Set(env.items.flatMap(r => r.states))].sort();
    const hasTargets = env.hasTargets;
    const anyHealth = env.items.some(r => r.health);
    const anySpark = env.items.some(r => r.sparkline.length > 1);
    const anyOot = env.hasPerOrderRepData && env.items.some(r => r.outOfTerritoryPct != null);
    const anyNew = env.items.some(r => r.newDealers != null);
    const tot = env.totals;
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Rep scorecards",
      hint: (() => {
        const act = env.items.filter(r => !isHouseRep(r) && isRosterRep(r)).length;
        const former = env.items.filter(r => !isRosterRep(r)).length;
        return `showing ${num(rows.length)} · ${num(act)} active reps${former ? `, ${num(former)} former` : ""} · click a rep for their detail`;
      })(),
      right: /*#__PURE__*/React.createElement("span", {
        className: "sx-chips"
      }, /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: this.openReport
      }, "Open as report"), /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: this.exportCsv
      }, "CSV"))
    }, this.renderFilters(/*#__PURE__*/React.createElement(React.Fragment, null, channels.length ? /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "150px",
      value: repChannel,
      onChange: v => this.setState({
        repChannel: v
      }),
      options: [["", "All channels"], ...channels.map(c => [c, channelLabel(c)])]
    }) : null, /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "150px",
      value: repRegion,
      onChange: v => this.setState({
        repRegion: v
      }),
      options: [["", "All states"], ...regions.map(s => [s, stateName(s)])]
    }), /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "130px",
      value: repStatus,
      onChange: v => this.setState({
        repStatus: v
      }),
      options: [["active", "Active"], ["", "All statuses"], ["terminated", "Terminated"]]
    }), hasTargets ? /*#__PURE__*/React.createElement("span", {
      className: "seg"
    }, [["", "Any"], ["yes", "On plan"], ["no", "Behind"]].map(([v, l]) => /*#__PURE__*/React.createElement("button", {
      key: v,
      className: repOnPlan === v ? "on" : "",
      onClick: () => this.setState({
        repOnPlan: v
      })
    }, l))) : null), /*#__PURE__*/React.createElement("span", {
      className: "seg"
    }, REP_SORTS.filter(([k]) => hasTargets || k !== "attainment" && k !== "pace").filter(([k]) => k !== "newDealers" || anyNew).filter(([k]) => k !== "atRisk" || anyHealth).map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      className: repSort === k || !hasTargets && repSort === "attainment" && k === "revenue" ? "on" : "",
      onClick: () => this.setState({
        repSort: k
      })
    }, l)))), env.source === "roster" ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, "Showing the rep list for now: goals, dealers slowing down, new dealers and the 12-month trend fill in shortly.") : null, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table sx-grid"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Rep"), /*#__PURE__*/React.createElement("th", {
      title: "Revenue booked in this period, with the change against the same dates last period.",
      className: "n"
    }, "Revenue"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Orders booked in this period."
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      title: "Dealers that ordered in this period, out of the dealers assigned to this rep.",
      className: "n"
    }, "Dealers ordering"), anyNew ? /*#__PURE__*/React.createElement("th", {
      title: "Dealers whose first order ever is in this period.",
      className: "n"
    }, "New") : null, hasTargets ? /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "This rep's goal for the period, from Targets."
    }, targetWindowLabel(range).replace(/^Target/, "Goal")) : /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "This rep's goal for the period, from Targets."
    }, isAdminUser() ? /*#__PURE__*/React.createElement(Link, {
      to: this.isRetail() ? "/Stores/targets" : "/Sales/targets"
    }, "Goal") : "Goal"), hasTargets ? /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Revenue so far against the goal for the same share of the period."
    }, "Progress to goal") : null, hasTargets ? /*#__PURE__*/React.createElement("th", {
      title: "Where this rep is on course to finish against the goal.",
      className: "n"
    }, "On course for") : null, anySpark ? /*#__PURE__*/React.createElement("th", {
      title: "Monthly revenue over the last twelve months.",
      className: "n"
    }, "Last 12 months") : null, anyHealth ? /*#__PURE__*/React.createElement("th", {
      title: "This rep's dealers ordering later than they usually do, and what they buy in a year."
    }, "Dealers slowing") : null, /*#__PURE__*/React.createElement("th", {
      title: "States this rep is assigned to, from Territories."
    }, "Area"), anyOot ? /*#__PURE__*/React.createElement("th", {
      title: "Share of this rep's sales at dealers outside their assigned states.",
      className: "n"
    }, "Outside their area") : null)), /*#__PURE__*/React.createElement("tbody", null, rows.map(r => /*#__PURE__*/React.createElement("tr", {
      key: r.repCode,
      className: "clickable" + (r.status === "terminated" ? " terminated" : ""),
      onClick: () => this.openRep(r.repCode),
      title: `Open ${r.name} rep detail`
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, r.name), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, r.repCode, r.channel ? ` · ${channelLabel(r.channel)}` : "", r.status === "terminated" ? " · terminated" : "", r.bookChanged ? " · " : "", r.bookChanged ? /*#__PURE__*/React.createElement(Pill, {
      tone: "warning"
    }, "book changed") : null)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sx-money"
    }, usdK(r.revenue), (() => {
      const d = kitDelta(deltaOfPaced(r.revenue, r.prev.revenue, range));
      return d ? /*#__PURE__*/React.createElement("span", {
        className: "sx-sub"
      }, /*#__PURE__*/React.createElement(Delta, {
        d: d
      })) : null;
    })())), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(r.orders)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(r.dealersActive), " ", /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "of ", num(r.dealersAssigned))), anyNew ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, r.newDealers != null ? num(r.newDealers) : "—") : null, /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, r.target ? usdK(r.target) : /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "not set")), hasTargets ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, r.target ? /*#__PURE__*/React.createElement(AttainBar, {
      ratio: r.attainment,
      tone: paceTone(r.pace)
    }) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "\u2014")) : null, hasTargets ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement(PaceText, {
      ratio: r.pace
    })) : null, anySpark ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement(Spark, {
      values: r.sparkline
    })) : null, anyHealth ? /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(HealthChips, {
      health: r.health
    })) : null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(StateChips, {
      states: r.states,
      onOpen: this.openRegion
    }), r.zips ? /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, " +", num(r.zips), " ZIPs") : null), anyOot ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, r.outOfTerritoryPct != null ? pct0(r.outOfTerritoryPct) : "—") : null)), tot ? /*#__PURE__*/React.createElement("tr", {
      className: "tot"
    }, /*#__PURE__*/React.createElement("td", null, num(env.items.length), " reps"), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, usdK(tot.revenue != null ? tot.revenue : tot.totalAmount)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(tot.orders)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, tot.dealersActive != null ? `${num(tot.dealersActive)} of ${num(tot.dealersAssigned)}` : ""), anyNew ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, tot.newDealers != null ? num(tot.newDealers) : "") : null, /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, tot.target ? usdK(tot.target) : ""), hasTargets ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, tot.attainment != null ? /*#__PURE__*/React.createElement(AttainBar, {
      ratio: Number(tot.attainment) > 5 ? Number(tot.attainment) / 100 : Number(tot.attainment),
      tone: ""
    }) : "") : null, hasTargets ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, tot.pace != null ? /*#__PURE__*/React.createElement(PaceText, {
      ratio: Number(tot.pace) > 5 ? Number(tot.pace) / 100 : Number(tot.pace)
    }) : "") : null, anySpark ? /*#__PURE__*/React.createElement("td", null) : null, anyHealth ? /*#__PURE__*/React.createElement("td", null, tot.atRisk != null ? `${num(tot.atRisk)} · ${usdK(tot.revenueAtRisk)} a year` : "") : null, /*#__PURE__*/React.createElement("td", null, tot.states != null ? `${num(tot.states)} states` : ""), anyOot ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, tot.outOfTerritoryPct != null ? pct0(Number(tot.outOfTerritoryPct) > 1 ? Number(tot.outOfTerritoryPct) / 100 : Number(tot.outOfTerritoryPct)) : "") : null) : null))), !rows.length ? /*#__PURE__*/React.createElement(Empty, null, env.items.length ? "No reps match these filters." : "No reps on the roster. Reps arrive from the sales rep feed (Manage Data, Connect).") : null, /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "Dealers slowing = the rep's dealers ordering later than they usually do, with what they buy in a year. Outside their area = share of the rep's sales at dealers outside their states (shows when orders carry the selling rep). Click a state to open it.")));
  }
  renderDealers(inProgress) {
    const env = this.state.dealers;
    const range = this.range();
    if (env === undefined) return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Pending, {
      h: 240
    })));
    if (env === null) return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Dealer scorecards"
    }, /*#__PURE__*/React.createElement(Empty, null, "The dealer rail is not available for this workspace yet. Dealer scorecards appear once the wholesale feeds (dealer accounts and orders) are connected.")));
    const rows = this.dealerRows();
    const totalRev = env.items.reduce((a, d) => a + (Number(d.revenue) || 0), 0);
    const {
      dlRep,
      dlRegion,
      dlChannel,
      dlBand,
      dlStage,
      dlSort
    } = this.state;
    const bands = this.bandCounts();
    const reps = [...new Map(env.items.filter(d => d.assignedRep).map(d => [d.assignedRep.repCode, d.assignedRep])).values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    const regions = [...new Set(env.items.map(d => d.region).filter(Boolean))].sort();
    const channels = [...new Set(env.items.map(d => d.channel).filter(Boolean))];
    const stages = [...new Set(env.items.map(d => d.stage).filter(Boolean))];
    const hasTargets = env.hasTargets;
    const anyHealth = env.items.some(d => d.health && d.health.band);
    const anySpark = env.items.some(d => d.sparkline.length > 1);
    const anyCats = env.items.some(d => d.categories.length);
    const toggleBand = b => this.setState({
      dlBand: dlBand === b ? "" : b
    });
    const fewer = env.items.filter(d => this.fewerOrders(d)).length;
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Dealer scorecards",
      hint: `showing ${num(rows.length)} of ${num(env.items.length)} · click a dealer for its detail`,
      right: /*#__PURE__*/React.createElement("span", {
        className: "sx-chips"
      }, /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: this.openReport
      }, "Open as report"), /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: this.exportCsv
      }, "CSV"))
    }, this.renderFilters(/*#__PURE__*/React.createElement(React.Fragment, null, bands ? LAPSE_LEVELS.map(([k, l, tone]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      className: `wa-pill ${tone} sx-bandchip${dlBand === k ? " on" : ""}`,
      onClick: () => toggleBand(k)
    }, l, " ", num(bands[k] || 0))) : null, bands && dlBand === "at_risk,lapsing" ? /*#__PURE__*/React.createElement("button", {
      className: "wa-pill danger sx-bandchip on",
      title: "Overdue and stopped ordering together",
      onClick: () => toggleBand("at_risk,lapsing")
    }, "Slowing down ", num((bands.at_risk || 0) + (bands.lapsing || 0))) : null, fewer ? /*#__PURE__*/React.createElement("button", {
      className: `wa-pill warning sx-bandchip${this.state.dlFewer ? " on" : ""}`,
      title: "Dealers with fewer orders than the same dates last period",
      onClick: () => this.setState({
        dlFewer: !this.state.dlFewer
      })
    }, "Fewer orders than last year ", num(fewer)) : null, this.state.dlNew ? /*#__PURE__*/React.createElement("button", {
      className: "wa-pill info sx-bandchip on",
      title: "Dealers whose first order ever is in this period; click to clear",
      onClick: () => this.setState({
        dlNew: false
      })
    }, "New dealers ", num(env.items.filter(d => d.raw && d.raw.newDealer).length)) : null, this.state.dlOther ? /*#__PURE__*/React.createElement("button", {
      className: "wa-pill neutral sx-bandchip on",
      title: "Dealers with orders sold by a rep who is not their own; click to clear",
      onClick: () => this.setState({
        dlOther: false
      })
    }, "Sold by another rep") : null, reps.length ? /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "170px",
      value: dlRep,
      onChange: v => this.setState({
        dlRep: v
      }),
      options: [["", "All reps"], ...reps.map(r => [r.repCode, r.name])]
    }) : null, /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "150px",
      value: dlRegion,
      onChange: v => this.setState({
        dlRegion: v
      }),
      options: [["", "All states"], ...regions.map(s => [s, stateName(s)])]
    }), channels.length ? /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "150px",
      value: dlChannel,
      onChange: v => this.setState({
        dlChannel: v
      }),
      options: [["", "All channels"], ...channels.map(c => [c, channelLabel(c)])]
    }) : null, stages.length ? /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "140px",
      value: dlStage,
      onChange: v => this.setState({
        dlStage: v
      }),
      options: [["", "All stages"], ...stages.map(s => [s, s])]
    }) : null), /*#__PURE__*/React.createElement("span", {
      className: "seg"
    }, DEALER_SORTS.filter(([k]) => hasTargets || k !== "attainment" && k !== "pace").filter(([k]) => k !== "band" || anyHealth).filter(([k]) => k !== "lastOrder" || env.items.some(d => d.lastOrder)).map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      className: dlSort === k || dlSort === "band" && !anyHealth && k === "revenue" ? "on" : "",
      onClick: () => this.setState({
        dlSort: k
      })
    }, l)))), env.source === "roster" ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, "Showing the dealer list for now: goals, health, category mix and the 12-month trend fill in shortly.") : null, (() => {
      // Standouts from the roster, no extra reads (ported from the retired /Dealers page).
      const all = env.items;
      const byAov = all.slice().sort((a, b) => (Number(b.aov) || 0) - (Number(a.aov) || 0))[0];
      const growth = d => d.prev && d.prev.revenue > 0 ? (d.revenue - d.prev.revenue) / d.prev.revenue : null;
      // Fastest growing among dealers with a real base last period (a $100 dealer going to $40K is not a story).
      const base = Math.max(1000, all.reduce((t, d) => t + (Number(d.prev && d.prev.revenue) || 0), 0) / Math.max(1, all.length) * 0.25);
      const fastest = all.filter(d => hasTargets ? d.pace != null : growth(d) != null && d.prev.revenue >= base && d.revenue > d.prev.revenue).sort((a, b) => hasTargets ? b.pace - a.pace : b.revenue - b.prev.revenue - (a.revenue - a.prev.revenue))[0];
      const slow = hasTargets ? all.filter(d => d.pace != null && d.pace < 1).sort((a, b) => a.pace - b.pace)[0] : null;
      if (!byAov && !fastest && !slow) return null;
      return /*#__PURE__*/React.createElement("div", {
        className: "sx-standouts"
      }, byAov && byAov.aov ? /*#__PURE__*/React.createElement("span", {
        title: "The dealer with the largest average order in this period."
      }, /*#__PURE__*/React.createElement("span", {
        className: "sx-k"
      }, "Biggest average order"), /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: () => this.openDealer(byAov.customerId || byAov.id)
      }, byAov.name), " ", usd0(byAov.aov)) : null, fastest ? /*#__PURE__*/React.createElement("span", {
        title: hasTargets ? "The dealer furthest ahead of its goal." : "The dealer whose sales grew the most against the same dates last period."
      }, /*#__PURE__*/React.createElement("span", {
        className: "sx-k"
      }, hasTargets ? "Furthest ahead of goal" : "Fastest growing"), /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: () => this.openDealer(fastest.customerId || fastest.id)
      }, fastest.name), " ", hasTargets ? `on course for ${pct0(fastest.pace)}` : `from ${usdK(fastest.prev.revenue)} to ${usdK(fastest.revenue)}`) : null, slow ? /*#__PURE__*/React.createElement("span", {
        title: "The dealer furthest behind its goal."
      }, /*#__PURE__*/React.createElement("span", {
        className: "sx-k"
      }, "Needs attention"), /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: () => this.openDealer(slow.customerId || slow.id)
      }, slow.name), " on course for ", pct0(slow.pace), " of goal") : null);
    })(), /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table sx-grid"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Dealer"), /*#__PURE__*/React.createElement("th", {
      title: "The rep assigned to this dealer."
    }, "Rep"), /*#__PURE__*/React.createElement("th", {
      title: "Revenue booked in this period, with the change against the same dates last period.",
      className: "n"
    }, "Revenue"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Orders booked in this period."
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "This dealer's share of all dealer revenue in this period."
    }, "Share"), hasTargets ? /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "This dealer's goal for the period, from Targets."
    }, "Goal") : /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "This dealer's goal for the period, from Targets."
    }, isAdminUser() ? /*#__PURE__*/React.createElement(Link, {
      to: "/Sales/targets?scope=dealer"
    }, "Goal") : "Goal"), hasTargets ? /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Revenue so far against the goal for the same share of the period."
    }, "Progress to goal") : null, hasTargets ? /*#__PURE__*/React.createElement("th", {
      title: "Where this dealer is on course to finish against the goal.",
      className: "n"
    }, "On course for") : null, anyHealth ? /*#__PURE__*/React.createElement("th", {
      title: "This dealer compared with how often it usually orders: on track, slowing, overdue or stopped ordering."
    }, "Health") : null, anyHealth ? /*#__PURE__*/React.createElement("th", {
      title: "How long since this dealer last ordered.",
      className: "n"
    }, "Last order") : null, anyHealth ? /*#__PURE__*/React.createElement("th", {
      title: "How often this dealer usually orders.",
      className: "n"
    }, "Usually every") : null, anyHealth ? /*#__PURE__*/React.createElement("th", {
      title: "What this dealer bought in the last twelve months, the yearly sales at stake if it stops ordering.",
      className: "n"
    }, "Yearly sales at stake") : null, anyCats ? /*#__PURE__*/React.createElement("th", {
      title: "Top categories by revenue in this period."
    }, "Category mix") : null, anySpark ? /*#__PURE__*/React.createElement("th", {
      title: "Monthly revenue over the last twelve months.",
      className: "n"
    }, "Last 12 months") : null)), /*#__PURE__*/React.createElement("tbody", null, rows.slice(0, this.state.dlLimit || 100).map(d => /*#__PURE__*/React.createElement("tr", {
      key: d.id,
      className: "clickable",
      onClick: () => this.openDealer(d.accountNumber || d.id),
      title: `Open ${d.name} dealer detail`
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, d.name), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, [d.accountNumber, [d.city, d.region].filter(Boolean).join(", "), d.channel ? channelLabel(d.channel) : ""].filter(Boolean).join(" · "))), /*#__PURE__*/React.createElement("td", null, d.assignedRep ? /*#__PURE__*/React.createElement("a", {
      className: "wa-rowlink",
      onClick: e => {
        e.stopPropagation();
        this.openRep(d.assignedRep.repCode);
      }
    }, d.assignedRep.name) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "no rep"), d.sellingReps > 1 ? /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, " \xB7 ", num(d.sellingReps), " selling") : null), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sx-money"
    }, usdK(d.revenue), (() => {
      const x = kitDelta(deltaOfPaced(d.revenue, d.prev.revenue, range));
      return x ? /*#__PURE__*/React.createElement("span", {
        className: "sx-sub"
      }, /*#__PURE__*/React.createElement(Delta, {
        d: x
      })) : d.raw && d.raw.newDealer ? /*#__PURE__*/React.createElement("span", {
        className: "sx-sub"
      }, /*#__PURE__*/React.createElement("span", {
        className: "wa-d flat"
      }, "new")) : null;
    })())), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(d.orders)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, totalRev > 0 ? pct0(d.revenue / totalRev) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "\u2014")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, d.target ? usdK(d.target) : /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "not set")), hasTargets ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, d.target ? /*#__PURE__*/React.createElement(AttainBar, {
      ratio: d.attainment,
      tone: paceTone(d.pace)
    }) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "\u2014")) : null, hasTargets ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement(PaceText, {
      ratio: d.pace
    })) : null, anyHealth ? /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(BandChip, {
      health: d.health
    })) : null, anyHealth ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, d.health && d.health.daysSinceOrder != null ? Number(d.health.daysSinceOrder) <= 0 ? "today" : `${num(d.health.daysSinceOrder)} ${Number(d.health.daysSinceOrder) === 1 ? "day" : "days"} ago` : "—") : null, anyHealth ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, d.health && d.health.cadenceDays != null ? `${num(d.health.cadenceDays)} days` : "—") : null, anyHealth ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, d.health && d.health.revenueAtStake ? usdK(d.health.revenueAtStake) : "—") : null, anyCats ? /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(CategoryChips, {
      categories: d.categories,
      onOpen: c => this.openCategory(d, c)
    })) : null, anySpark ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement(Spark, {
      values: d.sparkline
    })) : null))))), rows.length > (this.state.dlLimit || 100) ? /*#__PURE__*/React.createElement("div", {
      className: "sx-more"
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-flat k-button-flat-base",
      onClick: () => this.setState({
        dlLimit: (this.state.dlLimit || 100) + 100
      })
    }, "Show 100 more (", num(rows.length - (this.state.dlLimit || 100)), " left)")) : null, !rows.length ? /*#__PURE__*/React.createElement(Empty, null, env.items.length ? "No dealers match these filters." : "No dealer orders in this window. Dealers arrive from the wholesale sales feed.") : null, rows.length > 500 ? /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "Showing the first 500 of ", num(rows.length), ". Narrow the filters or export the CSV for the full list.") : null, /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "Health compares how long since each dealer last ordered with how often it usually orders. Dealer goals are optional; most businesses set them for their top 50 dealers.")), this.state.dlMatrix && this.state.dlMatrix.length > 0 && rows.length > 0 ? (() => {
      // Dealer x category compare (ported from the retired /Dealers page): top 12 categories, top 12 dealers in the current sort.
      const top = rows.slice(0, 12);
      const cols = top.map(d => d.name);
      const idOf = {};
      top.forEach(d => {
        idOf[d.name] = d.customerId || d.accountNumber || d.id;
      });
      const cell = (r, c) => Number(r.byStore && r.byStore[idOf[c]] != null ? r.byStore[idOf[c]] : r.byDealer && r.byDealer[idOf[c]] != null ? r.byDealer[idOf[c]] : (r.byStore || r.byDealer || {})[c]) || 0;
      const mrows = this.state.dlMatrix.map(r => ({
        cat: r.category,
        raw: r,
        total: Object.values(r.byStore || r.byDealer || {}).reduce((a, v) => a + (Number(v) || 0), 0)
      })).sort((a, b) => b.total - a.total).slice(0, 12);
      return /*#__PURE__*/React.createElement(Card, {
        title: "Category mix by dealer",
        hint: `top 12 categories · top ${top.length} dealers in the current sort · shading is each row's share across dealers`,
        span: 12
      }, /*#__PURE__*/React.createElement("div", {
        className: "tblwrap"
      }, /*#__PURE__*/React.createElement("table", {
        className: "territory-table wa-table"
      }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
        title: "Product category (leaf)"
      }, "Category"), cols.map(c => /*#__PURE__*/React.createElement("th", {
        key: c,
        className: "n",
        title: "Revenue in this category at this dealer"
      }, c)))), /*#__PURE__*/React.createElement("tbody", null, mrows.map(r => {
        const max = Math.max(1, ...cols.map(c => cell(r.raw, c)));
        return /*#__PURE__*/React.createElement("tr", {
          key: r.cat
        }, /*#__PURE__*/React.createElement("td", {
          title: decodeEntities(r.cat)
        }, categoryLeaf(r.cat)), cols.map(c => {
          const v = cell(r.raw, c);
          const heat = v / max;
          return /*#__PURE__*/React.createElement("td", {
            key: c,
            className: "n",
            style: heat > 0 ? {
              background: `rgba(255,99,88,${(0.06 + 0.2 * heat).toFixed(2)})`
            } : undefined
          }, v > 0 ? usdK(v) : "");
        }));
      })))));
    })() : null);
  }
  renderStores(inProgress) {
    const env = this.state.stores;
    const range = this.range();
    if (env === undefined) return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Pending, {
      h: 240
    })));
    if (env === null) return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Store scorecards"
    }, /*#__PURE__*/React.createElement(Empty, null, "Store performance is not available for this workspace yet. It needs the store master and the sales feed with a store id per order.")));
    const rows = env.items.slice().sort((a, b) => b.revenue - a.revenue);
    const hasTargets = env.hasTargets;
    const anyCats = rows.some(s => s.categories.length);
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Store scorecards",
      hint: `${num(rows.length)} stores · click a store for its detail`,
      right: /*#__PURE__*/React.createElement("span", {
        className: "sx-chips"
      }, /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: this.openReport
      }, "Open as report"), /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: this.exportCsv
      }, "CSV"), isAdminUser() ? /*#__PURE__*/React.createElement(Link, {
        className: "wa-rowlink",
        to: "/Stores/targets"
      }, "Store targets") : null)
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table sx-grid"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Store"), /*#__PURE__*/React.createElement("th", {
      title: "Revenue booked in this period, with the change against the same dates last period.",
      className: "n"
    }, "Revenue"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Orders booked in this period."
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      title: "Average order in this period.",
      className: "n"
    }, "Average order"), hasTargets ? /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Revenue so far against the goal for the same share of the period."
    }, "Progress to goal") : /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "This store's goal for the period, from Targets."
    }, isAdminUser() ? /*#__PURE__*/React.createElement(Link, {
      to: "/Stores/targets"
    }, "Goal") : "Goal"), hasTargets ? /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Where this store is on course to finish against the goal."
    }, "On course for") : null, anyCats ? /*#__PURE__*/React.createElement("th", {
      title: "Top categories by revenue in the window"
    }, "Category mix") : null, /*#__PURE__*/React.createElement("th", {
      title: "Whether the store is on course, or its customers are ordering later than usual."
    }, "Health"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(s => /*#__PURE__*/React.createElement("tr", {
      key: s.id,
      className: "clickable",
      onClick: () => this.openStore(s.id),
      title: `Open ${s.name} store detail`
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, s.name), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, /*#__PURE__*/React.createElement(Pill, {
      tone: s.type === "outlet" ? "warning" : "info"
    }, s.type === "outlet" ? "outlet" : "store"))), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sx-money"
    }, usdK(s.revenue), (() => {
      const x = kitDelta(deltaOfPaced(s.revenue, s.prev.revenue, range));
      return x ? /*#__PURE__*/React.createElement("span", {
        className: "sx-sub"
      }, /*#__PURE__*/React.createElement(Delta, {
        d: x
      })) : null;
    })())), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(s.orders)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, s.aov != null ? usd0(s.aov) : ""), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, hasTargets ? s.target ? /*#__PURE__*/React.createElement(AttainBar, {
      ratio: s.attainment,
      tone: paceTone(s.pace)
    }) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "\u2014") : s.target ? usdK(s.target) : /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "not set")), hasTargets ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement(PaceText, {
      ratio: s.pace
    })) : null, anyCats ? /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(CategoryChips, {
      categories: s.categories
    })) : null, /*#__PURE__*/React.createElement("td", null, hasTargets && s.pace != null && s.pace < 0.9 ? /*#__PURE__*/React.createElement("span", {
      className: "sx-chips"
    }, /*#__PURE__*/React.createElement(Pill, {
      tone: "warning"
    }, "needs attention"), /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "behind plan")) : s.health && s.health.atRisk ? /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "customers slowing down: ", num(s.health.atRisk + (s.health.lapsing || 0))) : /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "on track"))))))), !rows.length ? /*#__PURE__*/React.createElement(Empty, null, "No store orders in this window.") : null));
  }

  // Retail markets: ZIP3 areas ranked by shopper interest (web sessions) against orders booked there.
  // No reps, dealers or territory assignments: a retailer reads where its shoppers are and where they buy.
  marketRows() {
    const heat = this.state.geoHeat;
    if (!heat || !heat.length) return [];
    return heat.filter(z => (z.demand || z.demandIndex) > 0 || z.booked > 0).map(z => ({
      ...z,
      sessions: z.demand || 0
    })).sort((a, b) => (b.sessions || b.demandIndex) - (a.sessions || a.demandIndex) || (b.booked || 0) - (a.booked || 0));
  }
  marketRead(z) {
    if (this.state.geoHeat && this.state.geoHeat.bookedAvailable === false) return z.sessions ? "shopper interest" : "";
    if (z.sessions && !z.booked) return "browsing, not buying: a promotion or ad target";
    if (z.booked && !z.sessions) return "buying with little site traffic: repeat or in-store customers";
    if (z.bookedIndex != null && z.demandIndex != null && z.demandIndex - z.bookedIndex >= 25) return "interest well ahead of orders";
    return "buying in line with interest";
  }
  renderTopMarkets() {
    if (this.state.miMarkets === undefined) setTimeout(() => this.loadMiMarkets(), 0);
    const heat = this.state.geoHeat;
    const rows = this.marketRows().slice(0, 5);
    return /*#__PURE__*/React.createElement(Card, {
      title: "Top markets",
      hint: "where shoppers browse the site, and what they order there",
      span: 4,
      right: /*#__PURE__*/React.createElement(Link, {
        className: "wa-rowlink",
        to: this.path(this.range(), "coverage")
      }, "Markets tab")
    }, heat === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 140
    }) : !rows.length ? /*#__PURE__*/React.createElement(Empty, null, "Markets fill in once the site has visitors with a known location.") : /*#__PURE__*/React.createElement("table", {
      className: "territory-table sx-mini"
    }, /*#__PURE__*/React.createElement("tbody", null, rows.map(z => /*#__PURE__*/React.createElement("tr", {
      key: z.zip3,
      className: this.miMarketFor(z) ? "clickable" : "",
      onClick: () => this.openMiMarket(z),
      title: this.miMarketFor(z) ? `Open ${this.miMarketFor(z)} in Market Intelligence` : this.marketRead(z)
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, this.placeLabel(z)), " ", /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, num(z.sessions), " sessions")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, z.booked ? usdK(z.booked) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, heat && heat.bookedAvailable === false ? "" : "no orders")))))), heat && heat.bookedAvailable === false && rows.length ? /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "Orders by area need customer ZIP codes in your customer feed.") : null);
  }
  renderMarkets() {
    const {
      geoHeat,
      geoStores
    } = this.state;
    if (geoHeat === undefined) return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Pending, {
      h: 240
    })));
    const rows = this.marketRows();
    const buying = rows.filter(z => z.booked > 0);
    const browsing = rows.filter(z => z.sessions > 0 && !z.booked);
    const top = rows[0];
    const topGap = browsing[0];
    // No customer ZIPs on file: orders cannot be placed on the map, so "0 buying" would be wrong.
    const noZips = geoHeat && geoHeat.bookedAvailable === false;
    const tiles = rows.length ? [{
      l: "Top market",
      t: "The area with the most website sessions in the window",
      v: top ? this.placeLabel(top) : "—",
      s: top ? `${num(top.sessions)} sessions${noZips ? "" : ` · ${top.booked ? usdK(top.booked) + " ordered" : "no orders"}`}` : ""
    }, noZips ? {
      l: "Markets buying",
      t: "Areas (ZIP3) with at least one order in the window. Needs a ZIP code on each customer.",
      v: "Not known",
      s: "no customer ZIP code matched these orders"
    } : {
      l: "Markets buying",
      t: "Areas (ZIP3) with at least one order in the window",
      v: num(buying.length),
      s: buying.length ? `${usdK(buying.reduce((t, z) => t + (z.booked || 0), 0))} ordered` : ""
    }, noZips ? {
      l: "Areas browsing",
      t: "Areas with website sessions in the window",
      v: num(rows.filter(z => z.sessions > 0).length),
      s: top ? `top: ${this.placeLabel(top)} · ${num(top.sessions)} sessions` : ""
    } : {
      l: "Browsing, not buying",
      t: "Areas with website sessions and no orders in the window",
      v: num(browsing.length),
      s: topGap ? `top: ${this.placeLabel(topGap)} · ${num(topGap.sessions)} sessions` : ""
    }, {
      l: "Stores",
      t: "Store locations with an address on file",
      v: num(geoStores ? geoStores.length : 0)
    }] : [];
    const hasMap = geoHeat && geoHeat.length || geoStores && geoStores.length;
    return /*#__PURE__*/React.createElement(React.Fragment, null, tiles.length ? /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      tiles: tiles
    }) : null, /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Shopper markets",
      hint: "web interest by area (fill) \xB7 orders by the customer's home ZIP (ring) \xB7 store pins sized by in-store sales",
      span: 12
    }, hasMap ? /*#__PURE__*/React.createElement(GeoMap, {
      height: 420,
      stores: geoStores && geoStores.length ? geoStores : null,
      heat: geoHeat && geoHeat.length ? geoHeat : null,
      defaultLayers: {
        heat: true,
        stores: true
      },
      onStore: p => this.openStore(p.id),
      onHeat: z => this.openMiMarket(z)
    }) : /*#__PURE__*/React.createElement(Empty, null, "The map fills in once the site has visitors with a known location.")), rows.length ? /*#__PURE__*/React.createElement(Card, {
      title: "Markets",
      hint: "areas ranked by website sessions \xB7 an area inside one of your markets opens it in Market Intelligence",
      span: 12
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table sx-grid"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      title: "ZIP3 prefix area, named after its largest place"
    }, "Area"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Website sessions from this area in the window"
    }, "Web sessions"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Revenue from customers whose ship-to (or bill-to) ZIP is in this area, wherever they bought"
    }, "Ordered"), /*#__PURE__*/React.createElement("th", {
      title: "What the numbers say"
    }, "Read"))), /*#__PURE__*/React.createElement("tbody", null, rows.slice(0, 50).map(z => /*#__PURE__*/React.createElement("tr", {
      key: z.zip3,
      className: this.miMarketFor(z) ? "clickable" : "",
      onClick: () => this.openMiMarket(z),
      title: this.miMarketFor(z) ? `Open ${this.miMarketFor(z)} in Market Intelligence` : ""
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, this.placeLabel(z)), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, "ZIP ", z.zip3, this.miMarketFor(z) ? ` · market: ${this.miMarketFor(z)}` : "")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement("b", null, num(z.sessions))), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, z.booked ? usdK(z.booked) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, noZips ? "" : "none")), /*#__PURE__*/React.createElement("td", {
      className: "hint"
    }, this.marketRead(z)))))))) : null));
  }
  renderCoverage(retail) {
    const {
      coverage,
      geoSummary,
      geoCoverage,
      geoDealers,
      geoHeat,
      geoStores,
      covMetric,
      covView
    } = this.state;
    const noZips = geoHeat && geoHeat.bookedAvailable === false;
    const range = this.range();
    if (coverage === undefined) return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Pending, {
      h: 240
    })));
    const rows = this.coverageRows();
    const sum = coverage ? coverage.summary : null;
    const gs = geoSummary || null;
    const zipGrain = !!(gs || geoCoverage && geoCoverage.hexes.length);
    // Coverage strip (board 1, "Coverage tab strip"): plain labels, a next step on each tile.
    const admin = isAdminUser();
    const scrollTo = id => {
      try {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      } catch (e) {/* no DOM */}
    };
    const pick = k => gs ? gs[k] : geoCoverage && geoCoverage.summary ? geoCoverage.summary[k] : null;
    const hotU = gs && gs.hottestUncovered;
    const hotName = hotU ? String(hotU.label || "").replace(/\s*\(\d{3}\)\s*$/, "").replace(/ area$/i, "") : "";
    const overlaps = Number(pick("overlaps")) || 0;
    const uncZips = Number(pick("uncoveredZips")) || 0;
    const tiles = zipGrain ? [{
      l: "ZIP codes with a rep",
      t: "ZIP codes assigned to a rep in Territories.",
      v: num(pick("coveredZips")),
      s: gs && gs.zipsWithDealers != null ? `of ${num(gs.zipsWithDealers)} ZIP codes where you have dealers` : ""
    }, {
      l: "Dealers with no rep nearby",
      t: "Areas (first three digits of the ZIP) with dealers and no rep assigned, and what those dealers bought.",
      v: uncZips ? `${num(uncZips)} ${uncZips === 1 ? "area" : "areas"}` : "None",
      s: uncZips ? tileSub(gs && gs.uncoveredRevenue != null ? `${usdK(gs.uncoveredRevenue)} sold there in the last 12 months` : "", /*#__PURE__*/React.createElement(Go, {
        onClick: () => scrollTo("sc-cov-map")
      }, "See areas")) : "every area with dealers has a rep"
    }, {
      l: "Shared by two reps",
      t: "ZIP codes assigned to more than one rep on the same channel, with no lead rep set.",
      v: overlaps ? `${num(overlaps)} ZIP ${overlaps === 1 ? "code" : "codes"}` : "None",
      s: overlaps ? tileSub("assigned to more than one rep with no lead set", admin ? /*#__PURE__*/React.createElement(Go, {
        to: "/Data/Territories"
      }, "Fix in Territories") : null) : "each ZIP code has one rep"
    }, {
      l: "Most interest, no rep",
      t: "The area with the most website visits where no rep is assigned.",
      v: hotU ? `ZIP ${hotU.zip3 || ""}${hotName ? ` · ${hotName}` : ""}` : geoSummary === undefined ? "…" : "None",
      s: hotU ? `${num(hotU.demand != null ? hotU.demand : hotU.demandIndex)} website visits${Number(hotU.booked) > 0 ? ` · ${usdK(hotU.booked)} ordered` : ""}` : geoSummary === undefined ? "" : "every area with visits has a rep"
    }] : sum ? [{
      l: "States with a rep",
      t: "States with at least one rep assigned in Territories.",
      v: num(sum.covered)
    }, {
      l: "States with no rep",
      t: "States with dealers or sales and no rep assigned.",
      v: num(sum.uncovered),
      s: sum.uncoveredRevenue ? `${usdK(sum.uncoveredRevenue)} sold there ${inWindow(range)}` : ""
    }, {
      l: "Shared by two reps",
      t: "States assigned to more than one rep on the same channel, with no lead rep set.",
      v: num(sum.overlap),
      s: sum.overlapStates.length ? tileSub(sum.overlapStates.join(", "), admin ? /*#__PURE__*/React.createElement(Go, {
        to: "/Data/Territories"
      }, "Fix in Territories") : null) : "each state has one rep"
    }, {
      l: "Sold by another rep",
      t: "Share of sales booked by a rep outside their assigned states.",
      v: sum.revenue ? pct0(sum.outOfTerritoryRevenue / sum.revenue) : "Not tracked",
      s: sum.revenue ? usdK(sum.outOfTerritoryRevenue) : "needs the selling rep on each order"
    }] : [];
    const geoRows = rows.map(s => ({
      code: s.region,
      revenue: covMetric === "uncovered" ? s.noRepRevenue : covMetric === "reps" ? s.reps.length : s.revenue,
      orders: s.reps.length
    }));
    const mapAvailable = geoCoverage && geoCoverage.hexes.length || geoDealers && geoDealers.length || geoHeat && geoHeat.length || geoStores && geoStores.length;
    const geoPending = geoCoverage === undefined || geoDealers === undefined;
    return /*#__PURE__*/React.createElement(React.Fragment, null, tiles.length ? /*#__PURE__*/React.createElement("div", {
      className: "sc-strip"
    }, /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      cols: 4,
      tiles: tiles
    })) : null, /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      cls: "sc-anchor",
      title: /*#__PURE__*/React.createElement("span", {
        id: "sc-cov-map"
      }, retail ? "Store trade areas" : "Coverage map"),
      hint: zipGrain ? "rep areas by ZIP code · dealer pins sized by revenue, coloured by health" : "by state · the ZIP map fills in once territories carry ZIP codes",
      span: 12,
      right: /*#__PURE__*/React.createElement("span", {
        className: "sx-chips"
      }, mapAvailable ? /*#__PURE__*/React.createElement("span", {
        className: "seg"
      }, [["map", "Coverage map"], ["states", "States"]].map(([v, l]) => /*#__PURE__*/React.createElement("button", {
        key: v,
        className: covView === v ? "on" : "",
        onClick: () => this.setState({
          covView: v
        })
      }, l))) : null, (covView === "states" || !mapAvailable) && !retail ? /*#__PURE__*/React.createElement(PlunkDropDown, {
        width: "170px",
        value: covMetric,
        onChange: v => this.setState({
          covMetric: v
        }),
        options: [["revenue", "Revenue"], ["uncovered", "Sales with no rep"], ["reps", "Reps covering"]]
      }) : null)
    }, geoPending && covView === "map" ? /*#__PURE__*/React.createElement(Pending, {
      h: 380
    }) : mapAvailable && covView === "map" ? /*#__PURE__*/React.createElement(GeoMap, {
      height: 420,
      coverage: geoCoverage && geoCoverage.hexes.length ? geoCoverage : null,
      dealers: geoDealers && geoDealers.length ? geoDealers : null,
      stores: geoStores && geoStores.length ? geoStores : null,
      heat: geoHeat && geoHeat.length ? geoHeat : null,
      defaultLayers: {
        coverage: !retail,
        dealers: !retail,
        heat: false,
        stores: retail
      },
      onDealer: p => retail ? this.openStore(p.id) : this.openDealer(p.accountNumber || p.id),
      onStore: p => this.openStore(p.id),
      onRep: code => this.openRep(code),
      onHeat: z => z.state && this.openRegion(z.state)
    }) : geoRows.length ? /*#__PURE__*/React.createElement(GeoChoropleth, {
      rows: geoRows,
      metric: covMetric === "reps" ? "orders" : "revenue",
      labels: {
        revenue: covMetric === "uncovered" ? "Sales with no rep" : "Revenue",
        orders: "Reps covering"
      },
      onSelect: this.openRegion
    }) : /*#__PURE__*/React.createElement(Empty, null, coverage === null ? "The coverage rail is not available for this workspace yet." : "No territory data yet. Upload ZIP or state assignments in Manage Data, Territories, and dealers will map by ship-to ZIP."), !zipGrain && mapAvailable ? null : geoCoverage === null && !retail ? /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "ZIP-grain coverage needs territory rows with ZIPs. ", isAdminUser() ? /*#__PURE__*/React.createElement(Link, {
      to: "/Data/Territories"
    }, "Upload ZIP to rep assignments") : "An administrator can upload them in Manage Data.") : null), /*#__PURE__*/React.createElement(Card, {
      title: retail ? "Stores by state" : "Coverage by state",
      hint: "click a state for its market detail",
      span: 12,
      right: /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: this.exportCsv
      }, "CSV")
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table sx-grid"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      title: "State or province, from the dealer address and the territory table"
    }, "State"), /*#__PURE__*/React.createElement("th", {
      title: "Reps assigned to this state in the territory table"
    }, "Reps"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Dealer accounts with an address in this state"
    }, "Dealers"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Dealers that ordered in the window"
    }, "Active"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Revenue booked in the window at dealers in this state"
    }, "Revenue"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Revenue booked at accounts with no assigned rep"
    }, "No rep"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Revenue booked by reps whose area does not include this state."
    }, "Sold by another rep"), /*#__PURE__*/React.createElement("th", {
      title: "No rep: dealers or sales and no rep. Shared: two reps on the same channel with no lead set. No sales: a rep but no sales."
    }, "To look at"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(s => /*#__PURE__*/React.createElement("tr", {
      key: s.region,
      className: "clickable",
      onClick: () => this.openRegion(s.region),
      title: `Open ${stateName(s.region)} market detail`
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, s.region)), /*#__PURE__*/React.createElement("td", null, s.reps.length ? /*#__PURE__*/React.createElement("span", {
      className: "sx-chips"
    }, s.reps.slice(0, 3).map(r => /*#__PURE__*/React.createElement("button", {
      key: r.repCode,
      className: "sx-state",
      onClick: e => {
        e.stopPropagation();
        this.openRep(r.repCode);
      },
      title: `${r.name} (${r.repCode})`
    }, r.name && r.name !== r.repCode ? r.name : r.repCode)), s.reps.length > 3 ? /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "+", s.reps.length - 3) : null) : /*#__PURE__*/React.createElement(Pill, {
      tone: "danger"
    }, "none")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(s.dealers)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(s.activeDealers)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, usdK(s.revenue)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, s.noRepRevenue ? /*#__PURE__*/React.createElement("span", {
      className: "wa-d down"
    }, usdK(s.noRepRevenue)) : "$0"), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, usdK(s.outOfTerritoryRevenue)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "sx-chips"
    }, s.flags.map(f => /*#__PURE__*/React.createElement(Pill, {
      key: f,
      tone: f === "uncovered" ? "danger" : f === "overlap" ? "warning" : "neutral"
    }, flagLabel(f)))))))))), !rows.length ? /*#__PURE__*/React.createElement(Empty, null, coverage === null ? "The coverage rail is not available yet." : "No dealer ship-to states in this window.") : null, coverage && coverage.source === "roster" ? /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "Reading the territories rollup and rep roster until the coverage rail is live: out-of-territory revenue and overlap flags arrive with it.") : null), geoHeat && geoHeat.length && this.uncoveredDemand(25).length ? /*#__PURE__*/React.createElement(Card, {
      title: "Website interest with no rep",
      hint: "areas people browse from where no rep is assigned, most visits first \xB7 assign them in Manage Data, Territories",
      span: 12,
      right: isAdminUser() ? /*#__PURE__*/React.createElement(Link, {
        className: "wa-rowlink",
        to: "/Data/Territories"
      }, "Assign territories") : null
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table sx-grid"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      title: "ZIP3 prefix area, named after its largest place"
    }, "Area"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Website visits from this area in this period."
    }, "Website visits"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Revenue booked at dealers in this area in this period."
    }, "Ordered"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Dealer accounts with an address in this area"
    }, "Dealers"), /*#__PURE__*/React.createElement("th", {
      title: "What the numbers say"
    }, "Read"))), /*#__PURE__*/React.createElement("tbody", null, this.uncoveredDemand(25).map(z => /*#__PURE__*/React.createElement("tr", {
      key: z.zip3,
      className: z.state ? "clickable" : "",
      onClick: () => z.state && this.openRegion(z.state)
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, this.placeLabel(z)), /*#__PURE__*/React.createElement("span", {
      className: "code"
    }, "ZIP ", z.zip3)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement("b", null, num(z.demand || z.demandIndex))), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, z.booked ? usdK(z.booked) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, noZips ? "" : "none")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(z.dealers)), /*#__PURE__*/React.createElement("td", {
      className: "hint"
    }, z.dealers && !z.booked ? "dealers here, no orders and no rep: worth a visit" : z.dealers ? "dealers buying with no rep: assign one" : "interest with no dealer and no rep: a place to find dealers"))))))) : null, geoHeat && geoHeat.length ? /*#__PURE__*/React.createElement(Card, {
      title: "Interest against orders",
      hint: "website interest against what dealers order, by area \xB7 a big gap with no rep is where the next rep goes",
      span: 12
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table sx-grid"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      title: "Area by the first three digits of the ZIP code, named after its largest place."
    }, "Area"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Website visits from this area, scored 0 to 100 against every other area (100 = the most)."
    }, "Interest score"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "What dealers here ordered, scored 0 to 100 the same way."
    }, "Orders score"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Interest score minus orders score: interest that orders do not yet match."
    }, "Gap"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Dealers with an address in this area."
    }, "Dealers"), /*#__PURE__*/React.createElement("th", {
      title: "Reps assigned to ZIP codes in this area."
    }, "Reps"), /*#__PURE__*/React.createElement("th", {
      title: "What the numbers say"
    }, "Read"))), /*#__PURE__*/React.createElement("tbody", null, geoHeat.slice().sort((a, b) => b.gap - a.gap).slice(0, 12).map(z => /*#__PURE__*/React.createElement("tr", {
      key: z.zip3,
      className: z.state ? "clickable" : "",
      onClick: () => z.state && this.openRegion(z.state)
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, z.label)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(z.demandIndex)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(z.bookedIndex)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement("b", null, num(z.gap))), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(z.dealers)), /*#__PURE__*/React.createElement("td", null, z.uncovered ? /*#__PURE__*/React.createElement(Pill, {
      tone: "danger"
    }, "No rep") : z.reps.length ? /*#__PURE__*/React.createElement("span", {
      className: "sx-chips"
    }, z.reps.slice(0, 3).map(r => /*#__PURE__*/React.createElement("span", {
      key: r,
      className: "sx-state"
    }, r))) : /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, num(z.repCount), " assigned")), /*#__PURE__*/React.createElement("td", {
      className: "hint"
    }, z.uncovered ? "interest with no rep here" : z.gap > 20 ? "has a rep, orders lag interest: a call list" : "in balance"))))))) : null));
  }
}
export default withRouter(SalesExecutiveView);